import React, { useMemo} from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DateTimePicker } from '@material-ui/pickers';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';

import ElectionPeriods from 'app/utils/ElectionPeriods';

const renderDateInput = props => (
    <TextField
        variant="outlined"
        fullWidth
        onClick={props.onClick}
        id={props.id}
        value={props.value}
        disabled={props.disabled}
        label={props.label}
        helperText={props.helperText}
        {...props.inputProps}
    />
);

const useStyles = makeStyles(theme => ({
    section: {
        marginBottom: theme.spacing(3)
    },
    formContainer: {
        overflowY: 'scroll',
        height: '100%',
        padding: theme.spacing(3)
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    image: {
        maxHeight: 300,
        maxWidth: 300,
        height: 'auto',
        width: '100%'
    },
    imageContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: theme.spacing(6)
    },
    height: {
        height: '100%'
    },
    settingsContainer: {
        height: '100%',
        padding: theme.spacing(3)
    },
    opacity: {
        opacity: 0.5
    }
}));

export default function ElectionDetails(props) {

    const {
        election,
        handleElection,
        isError,
        getErrorMessage,
        errors,
        newElection
    } = props;

    const classes = useStyles();



    const getSingularMessage = () => {
        if (isError('button_text')) return errors.button_text;
        return "Used for the New Proposal button and to describe individual proposals";
    }

    const getDateFormat = () => {
        switch (election.repeat_period) {
            case 'years':
                return 'y';
            case 'months':
                return 'LLLL y';
            case 'weeks':
            case 'days':
                return 'DDD';
            default: return '';
        }
    };

    const electionNameWithDate = useMemo(() => {
        if (election.repeated) {

            const dateFormat = getDateFormat();

            switch (election.add_date_to_name) {
                case 0: return election.name;
                case 1:
                    return election.starts_at.toFormat(dateFormat) + ' ' + election.name;
                case 2:
                    return election.name + ' ' + election.starts_at.toFormat(dateFormat);
            }
        } else {
            return election.name;
        }

    }, [election.add_date_to_name, election.repeated, election.name, election.starts_at, election.repeat_period]);

    return (
        <Grid container direction="row" className={newElection ? classes.height : null}>
            {newElection &&
                <Grid item xs className={classes.height}>
                    <div className={classes.imageContainer}>
                        <img className={classes.image} src='/images/step-election.png' alt="election-graphic" />
                    </div>
                </Grid>
            }
            <Grid item xs={newElection ? 7 : 12} className={newElection ? classes.formContainer : classes.settingsContainer}>
                <div className={classes.section}>
                    <Typography variant="h6" gutterBottom>
                        {newElection ? "New Election" : "Election Title"}
                    </Typography>
                    <TextField
                        id="input-name"
                        label="Name"
                        value={election.name}
                        margin="normal"
                        onChange={handleElection('name')}
                        fullWidth
                        autoFocus
                        error={isError('name')}
                        helperText={getErrorMessage('name')}
                        variant="outlined"
                        required
                    />
                    <TextField
                        id="input-description"
                        label="Description"
                        name="description"
                        value={election.description}
                        onChange={handleElection('description')}
                        fullWidth
                        error={isError('description')}
                        helperText={getErrorMessage('description')}
                        variant="outlined"
                        multiline
                        margin="normal"
                        rows="4"
                        rowsMax="4"
                        inputProps={{
                            maxLength: "255"
                        }}
                        required
                    />
                    <TextField
                        id="input-button-name"
                        label="Singular Name"
                        value={election.button_name}
                        margin="normal"
                        onChange={handleElection('button_name')}
                        fullWidth
                        placeholder='i.e. "New Member"'
                        error={isError('button_name')}
                        helperText={getSingularMessage()}
                        variant="outlined"
                        required
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        What date and time should the election start on?
                    </Typography>
                    <DateTimePicker
                        inputVariant="outlined"
                        value={election.starts_at}
                        onChange={handleElection('starts_at')}
                        variant="outlined"
                        TextFieldComponent={renderDateInput}
                        minutesStep={15}
                    />
                </div>
                <div className={classes.section}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={election.repeated}
                                onChange={handleElection('repeated')}
                                color="primary"
                                value="repeated"
                            />
                        }
                        label="Should this election cycle?"
                        labelPlacement="end"
                        margin="normal"
                    />
                </div>
                <div className={! election.repeated ? classes.opacity : null}>
                    <div className={classes.section}>
                        <Typography variant='subtitle2' gutterBottom>
                            How often should the election cycle?
                        </Typography>
                        <Grid container spacing={3} alignItems="center" wrap="nowrap">
                            <Grid item>
                                <Typography variant='subtitle2' color="textSecondary" align="right">
                                    Every
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    id="input-repeat-amount"
                                    value={election.repeat_amount}
                                    onChange={handleElection('repeat_amount')}
                                    fullWidth
                                    type="number"
                                    error={isError('repeat_amount')}
                                    helperText={getErrorMessage('repeat_amount')}
                                    variant="outlined"
                                    disabled={! election.repeated}
                                />
                            </Grid>
                            <Grid item xs={8}>
                                <TextField
                                    id="input-repeat-period"
                                    select
                                    value={election.repeat_period}
                                    onChange={handleElection('repeat_period')}
                                    variant="outlined"
                                    fullWidth
                                    error={isError('repeat_period')}
                                    helperText={getErrorMessage('repeat_period')}
                                    disabled={! election.repeated}
                                    >
                                    {ElectionPeriods.map((period, i) => (
                                        <MenuItem key={i} value={period}>
                                            {period}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    </div>
                    <div className={classes.section}>
                        <Typography variant='subtitle2' gutterBottom>
                            Would you like to update the election name with the date every cycle?
                        </Typography>
                        <TextField
                            id="input-add-date-to-name"
                            select
                            value={election.add_date_to_name}
                            onChange={handleElection('add_date_to_name')}
                            variant="outlined"
                            fullWidth
                            error={isError('add_date_to_name')}
                            helperText={getErrorMessage('add_date_to_name')}
                            disabled={! election.repeated}
                            >
                            <MenuItem value={0}>
                                Do Not Update
                            </MenuItem>
                            <MenuItem value={1}>
                                Add to Beginning
                            </MenuItem>
                            <MenuItem value={2}>
                                Add to End
                            </MenuItem>
                        </TextField>
                        <Typography variant='subtitle2' display="inline" color="textSecondary">
                            The name will display as:&nbsp;&nbsp;&nbsp;
                        </Typography>
                        <Typography variant='subtitle2' display="inline" color="primary">
                            {electionNameWithDate}
                        </Typography>
                    </div>
                </div>
            </Grid>
        </Grid>
    )
}
